/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, Typography2, breakpoints, typography2 } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import type { MarketingContent } from 'bundles/degree-description/__generated__/baseContentfulTypes';
import Container from 'bundles/degree-description/components/shared/Container';
import { useDegreeDescriptionPageData } from 'bundles/degree-description/contexts/DegreeDescriptionPageContext';
import { useDegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';
import type { DegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';
import LazyImgix from 'bundles/page/components/shared/LazyImgix';

export type InfoListItem = Partial<MarketingContent>;

const styles = {
  infoListWrapper: css`
    margin: 32px 0;
  `,
  infoListItem: () => css`
    margin-bottom: 32px;
    padding-right: 16px;

    &:nth-of-type(2n) {
      padding-left: 16px;
      padding-right: 0;
    }

    ${breakpoints.down('sm')} {
      padding-left: 0;
      padding-right: 0;
      margin-bottom: 48px;

      &:nth-of-type(2n) {
        padding-left: 0;
        padding-right: 0;
      }
    }
  `,
  infoListImage: () => css`
    padding-right: 16px;

    ${breakpoints.down('xs')} {
      margin-bottom: 24px;
      padding-right: 0;
    }
  `,
  infoListText: (theme: DegreeDescriptionPageTheme) => css`
    padding-left: 16px;

    .rc-Markdown.styled {
      h1,
      h2,
      h3,
      h4,
      h5,
      h6,
      p,
      li {
        font-family: ${theme.typography.fontFamily};
      }

      p,
      li {
        ${typography2.bodyPrimary};
        color: var(--cds-color-grey-700);
        ${theme.mobileFontSizeStyle?.body};
      }

      a {
        color: var(--cds-color-blue-800);

        &:hover,
        &:focus {
          text-decoration: none;
        }

        &:visited {
          color: var(--cds-color-purple-900);
        }
      }
    }

    ${breakpoints.down('xs')} {
      padding-left: 0;
    }
  `,
  infoListTextTitle: css`
    margin-bottom: 4px;
  `,
  infoListLazyImg: css`
    border-radius: 4px;
    width: 100%;
  `,
};

type Props = {
  infoListItems: InfoListItem[];
};

const InfoListModule = ({ infoListItems }: Props) => {
  const theme = useDegreeDescriptionPageTheme();
  const { id } = useDegreeDescriptionPageData();
  const degreeId = id.replace(/^base~/, '');
  return (
    <Container>
      <Grid container css={styles.infoListWrapper}>
        {infoListItems?.map((item) => {
          const image = item?.visual;
          return (
            <Grid key={item?.sys?.id} container item xs={12} md={6} css={styles.infoListItem}>
              <Grid item xs={12} sm={5} css={styles.infoListImage}>
                {image?.url && image?.width && image?.height && (
                  <LazyImgix
                    css={styles.infoListLazyImg}
                    src={image.url}
                    alt={image?.description || ''}
                    maxWidth={image.width}
                    maxHeight={image.height}
                    enableFluidWidth={true}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={7} css={styles.infoListText(theme)}>
                <Typography2 component="h3" variant="subtitleMedium" css={styles.infoListTextTitle}>
                  {item?.title}
                </Typography2>
                <div>
                  <Markdown
                    source={item?.description}
                    trackingName="info_list_link"
                    buttonTrackingNameV3="degree_info_link"
                    trackingSection="page_body"
                    trackingProductId={degreeId}
                  />
                </div>
              </Grid>
            </Grid>
          );
        })}
      </Grid>
    </Container>
  );
};

export default React.memo(InfoListModule);
